import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  Text,
  toast,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { fromZonedTime } from 'date-fns-tz'
import { CompanyPicker } from '../../../components/CompanyPicker'
import { useChargesContextValue } from '../../../context/ChargesProvider'
import { useChargesWorkflowsContextValue } from '../../../context/ChargesWorkflowsProvider'
import { useGenerateInvoiceFlowContextValue } from '../context/GenerateInvoiceFlowProvider'
import {
  generateInvoiceFlowSchema,
  GenerateInvoiceFlowSchema,
} from '../schemas/generateInvoice'

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

function toPacificIso(datetimeLocalValue: string): string {
  return fromZonedTime(datetimeLocalValue, PACIFIC_TIMEZONE).toISOString()
}

export const GenerateInvoiceFlowMain = () => {
  const {
    generateInvoiceFlow: { isOpen, close },
  } = useChargesWorkflowsContextValue()

  const {
    state: { companyId: filteredCompanyId },
  } = useChargesContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useGenerateInvoiceFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm<GenerateInvoiceFlowSchema>({
    resolver: zodResolver(generateInvoiceFlowSchema),
    defaultValues: {
      companyId: undefined,
      startsAt: '',
      endsAt: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return

    reset({
      companyId: filteredCompanyId ?? undefined,
      startsAt: '',
      endsAt: '',
    })
  }, [isOpen, filteredCompanyId, reset])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        companyId: data.companyId,
        startsAt: toPacificIso(data.startsAt),
        endsAt: toPacificIso(data.endsAt),
      },
      {
        onSuccess: (result) => {
          const count = result.invoices.length
          toast.success(
            count === 0
              ? 'No invoices created for this filter'
              : `Created ${count} invoice${count === 1 ? '' : 's'}`,
            { position: 'top-center' }
          )
          handleClose()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Create invoices
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
        style={{ width: '100%' }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Generate invoice
        </Heading>
        <Text fontSize={1} color="lightText" mt={2}>
          Includes finalized, uninvoiced charges whose shift start falls in the
          selected time range. Times are always Pacific Time.
        </Text>

        <Box mt={4}>
          <Controller
            control={control}
            name="companyId"
            render={({ field, fieldState }) => (
              <CompanyPicker
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message?.toString()}
              />
            )}
          />
        </Box>

        <Field
          label="Start (Pacific Time)"
          error={errors.startsAt?.message?.toString()}
          // Intentionally disabled. This should work, unsure why it's throwing an error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          mt={4}
        >
          <Input
            block
            type="datetime-local"
            aria-label="Start, Pacific Time"
            {...register('startsAt')}
          />
        </Field>

        <Field
          label="End (Pacific Time)"
          error={errors.endsAt?.message?.toString()}
          // Intentionally disabled. This should work, unsure why it's throwing an error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          mt={4}
        >
          <Input
            block
            type="datetime-local"
            aria-label="End, Pacific Time"
            {...register('endsAt')}
          />
        </Field>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
